import { COMPANY_LAST_STAND, COMPANY_WARDEN } from '../company.js';
import { townsperson } from './content.js';
/**
 * The end of the story (ADR 0068 §4, `docs/LORE.md`): the Company musters at
 * its quarter, climbs to Shefira's Aire with the player and falls there, all
 * but Ulfar; Ser Givrecoeur plants his sword in her flank and she flees.
 * Back at the quarter, for that player alone, Ulfar keeps the memorial of
 * the five and makes them swear to finish her, which is no longer a fight
 * one wins alone (a raid of three to ten).
 *
 * The quests belong to the Pics' band (level 20) but to no region's road:
 * everything happens at Havrebourg and in the Aire. The townsfolk they add
 * are placed by the generator with the quarter (`world-company.ts`), not by
 * `keepers`: they are each player's own (`personal`, `solo`), raised once
 * the stand is made (`unlockedBy`).
 */
/** Ser Givrecoeur, at the Company's quarter in Havrebourg (ADR 0067). */
const GIVRECOEUR = { mob: 'tutor', biome: 'meadow' };
/** The recruits at the war table, each their recruiter's own (ADR 0067). */
const MAELLE = { mob: 'company-lookout', biome: 'meadow' };
const MELISANDE = { mob: 'company-forest-druid', biome: 'meadow' };
const NELLA = { mob: 'company-swamp-hunter', biome: 'meadow' };
const MERET = { mob: 'company-jackal-priestess', biome: 'meadow' };
const ULFAR_AT_THE_TABLE = { mob: 'company-snow-warden', biome: 'meadow' };
/** Ulfar after the stand, at Ser Givrecoeur's place by the memorial. */
const ULFAR = { mob: COMPANY_WARDEN, biome: 'meadow' };
const QUESTS = [
    {
        id: 'company-muster',
        name: 'La Compagnie se rassemble',
        biome: 'snow',
        level: 20,
        requires: [
            'meadow-recruit',
            'forest-recruit',
            'swamp-recruit',
            'desert-recruit',
            'snow-recruit',
        ],
        giver: GIVRECOEUR,
        turnIn: ULFAR_AT_THE_TABLE,
        offer: 'Cinq recrues ! Il y a un an, j’avais une tente trouée et tout le monde se moquait de moi. Méret sent la chaîne du dragon lâcher chaque nuit, et Maëlle voit sa lumière au-dessus de Givrecime. Le dragon a repris des forces. On ne va pas l’attendre ici ! Va prévenir chacun d’eux à la table, et dis-leur qu’on part. Puis va voir Ulfar, c’est lui qui organise tout.',
        summary: 'Prévenir Maëlle, Mélisande, Nella et Méret à la table de guerre, puis rendre compte au Vieil Ulfar.',
        pending: 'Tu les as tous prévenus ? Alors reviens me voir.',
        complete: 'Ils sont prêts ! Maëlle a compté ses flèches deux fois, Mélisande a fermé les yeux pour se concentrer, et Nella souriait jusqu’aux oreilles. Ça va être dur là-haut, gamin. Va dire à Givrecoeur que tout le monde est prêt.',
        objectives: [
            {
                kind: 'talk',
                label: 'Prévenir la Guetteuse Maëlle',
                npc: MAELLE,
                line: 'Chaque nuit, je vois sa lumière au-dessus des sommets, toujours à la même heure. Cette fois, je la verrai arriver, et je crierai assez tôt pour tout le monde !',
            },
            {
                kind: 'talk',
                label: 'Prévenir la Druidesse Mélisande',
                npc: MELISANDE,
                line: 'Là-haut, rien ne pousse, alors j’emporte des graines. Trois sacs ! Elles pousseront très vite si je le leur demande gentiment, et elles bloqueront les bébés dragons.',
            },
            {
                kind: 'talk',
                label: 'Prévenir la Traqueuse Nella',
                npc: NELLA,
                line: 'Enfin ! Des mois que je suis ses traces, je vais enfin la voir ! Mes harpons sont aiguisés, et une bête de cette taille, on ne peut pas la rater.',
            },
            {
                kind: 'talk',
                label: 'Prévenir la Prêtresse Méret',
                npc: MERET,
                line: 'Mes ancêtres avaient une chaîne et un géant pour retenir le dragon. Nous, on n’a plus ni l’un ni l’autre. Mais j’ai ma magie de protection, et je la tiendrai aussi longtemps qu’il le faudra.',
            },
        ],
        reward: { xp: 800, gold: 480 },
    },
    {
        id: COMPANY_LAST_STAND,
        name: 'La dernière garde',
        biome: 'snow',
        level: 20,
        requires: ['company-muster'],
        giver: GIVRECOEUR,
        turnIn: ULFAR,
        offer: 'Écoute bien, recrue. La crevasse de la Brèche mène au nid du dragon. On y va tous ensemble, et on l’attaque avant qu’il descende sur nos villes. Si l’un de nous tombe, tu continues, d’accord ? C’est tout ce que je te demande. Tant que le givre tient !',
        summary: 'Entrer dans l’Aire de Shefira par la crevasse de la Brèche, et tenir la dernière garde avec la Compagnie.',
        pending: 'Rejoins la crevasse de la Brèche, gamin. Givrecoeur est déjà parti devant !',
        complete: 'Tu es revenu ! Moi aussi, de justesse, on a dû me porter jusqu’ici. Quand je me suis réveillé, il n’y avait plus que toi debout. Maëlle, Mélisande, Nella, Méret… et Givrecoeur. Ils se sont battus jusqu’au bout. Givrecoeur a planté son épée dans le flanc du dragon, et la bête s’est enfuie vers les sommets avec l’épée. Regarde le monument : la flamme brûle toujours, mais l’épée n’y est plus.',
        objectives: [
            {
                kind: 'stand',
                label: 'Tenir la dernière garde à l’Aire de Shefira',
                zone: 'shefira-aerie',
            },
        ],
        reward: { xp: 2500, gold: 1500 },
    },
    {
        id: 'company-vow',
        name: 'Le serment des cinq',
        biome: 'snow',
        level: 20,
        requires: [COMPANY_LAST_STAND],
        giver: ULFAR,
        turnIn: ULFAR,
        offer: 'Cinq pierres pour nos amis. Moi, je suis trop vieux pour me battre encore, gamin. Mais toi, tu peux. Promets-le devant eux : tu retourneras au nid du dragon et tu le battras pour de bon. Tout seul, c’est impossible. Emmène des amis, au moins trois et jusqu’à dix, des gens qui ont tenu bon comme toi. Et quand il tombera, reprends l’épée de Givrecoeur.',
        summary: 'Remonter à l’Aire de Shefira en raid, de trois à dix, et abattre Shefira.',
        pending: 'Le dragon est toujours en vie. Mon genou me le dit à chaque fois que le vent descend des montagnes.',
        complete: 'Le dragon est tombé ! On a réussi ! Et l’épée est revenue. Garde-la, gamin, Givrecoeur te l’aurait donnée lui-même. Et ce petit dragon qui t’a suivi depuis le nid, couché à tes pieds ? C’est un bébé de sa couvée. Ces bêtes-là suivent celui qui a battu le plus fort d’entre eux. Monte-le ! Tant que le givre tient.',
        objectives: [{ kind: 'raid', label: 'Abattre Shefira en raid', count: 1 }],
        // Ser Givrecoeur's sword, back from her wound, and a drake of her brood
        // (ADR 0068 §6). The sword is raid gear (`source: 'raid'`): it is born
        // with its affixes like a dropped one, but bound, as every quest reward.
        reward: {
            xp: 3000,
            gold: 1800,
            items: [
                { templateId: 'givrecoeur-oath-legendary', quantity: 1 },
                { templateId: 'shefira-drake', quantity: 1 },
            ],
        },
    },
];
/** The townsfolk only the finale adds: Ulfar at the memorial, and its five steles. */
export const FINALE_NPC_IDS = [
    COMPANY_WARDEN,
    'stele-givrecoeur',
    'stele-maelle',
    'stele-melisande',
    'stele-nella',
    'stele-meret',
];
/**
 * A stele of the memorial: a stone, drawn from the prop's image as a body of
 * its own (`body.memorial-stele`), named after the one it stands for, who
 * answers a click with an epitaph and gives nothing.
 */
function stele(id, name) {
    return townsperson(id, name, { bodyKey: 'body.memorial-stele', mapIcon: undefined });
}
export const COMPANY_FINALE = {
    quests: QUESTS,
    npcs: {
        // The same old warden, body and name, at the quarter now.
        [COMPANY_WARDEN]: townsperson(COMPANY_WARDEN, 'Vieil Ulfar', { bodyKey: 'body.snow-warden' }),
        'stele-givrecoeur': stele('stele-givrecoeur', 'Ser Givrecoeur'),
        'stele-maelle': stele('stele-maelle', 'Maëlle'),
        'stele-melisande': stele('stele-melisande', 'Mélisande'),
        'stele-nella': stele('stele-nella', 'Nella'),
        'stele-meret': stele('stele-meret', 'Méret'),
    },
    keepers: [],
    items: [],
    xp: {},
    greetings: {
        [COMPANY_WARDEN]: 'Cinq pierres, un monument sans épée, et un vieil homme pour les garder. Tant que le givre tient.',
        'stele-givrecoeur': 'Ser Givrecoeur, fondateur de la Compagnie. Tout le monde riait de lui, et il avait raison. Tant que le givre tient.',
        'stele-maelle': 'Guetteuse Maëlle, de la Tour de Guet. Elle a vu le dragon la première, et elle a crié à temps.',
        'stele-melisande': 'Druidesse Mélisande, du Pic des Ramures. Ses plantes ont protégé ses amis jusqu’au bout.',
        'stele-nella': 'Traqueuse Nella, de Brumevase. Elle n’a jamais lâché son harpon.',
        'stele-meret': 'Prêtresse Méret, dernière gardienne du Chacal. Sa protection a tenu jusqu’au bout.',
    },
};
//# sourceMappingURL=company-finale.js.map